import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getMe } from '@web/features/auth/services/userApi'
import { clearTokens } from '@web/features/auth/utils/tokenStorage'
import i18n, { ACTIVE_LANGUAGES, setLanguage } from '@web/i18n'
import { publicSiteUrl } from '../utils/links'

export function UserMenu() {
  const { t } = useTranslation('gestion')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: ({ signal }) => getMe({ signal }), staleTime: 5 * 60_000 })

  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  const logout = () => {
    clearTokens()
    queryClient.clear()
    navigate('/connexion', { replace: true })
  }

  const label = me?.first_name || me?.username || '…'

  return (
    <div className="g-user" ref={ref}>
      <button type="button" className="g-user__button" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="g-user__avatar" aria-hidden>
          {label.slice(0, 1).toUpperCase()}
        </span>
        <span className="g-user__name">{label}</span>
        <span className="mdi mdi-chevron-down" aria-hidden />
      </button>
      {open && (
        <div className="g-user__menu" role="menu">
          {me?.email && <div className="g-user__email">{me.email}</div>}
          <div className="g-user__langs">
            {ACTIVE_LANGUAGES.map((l) => (
              <button
                key={l.code}
                type="button"
                className={`g-user__lang${i18n.language === l.code ? ' g-user__lang--active' : ''}`}
                onClick={() => void setLanguage(l.code)}
              >
                {l.label}
              </button>
            ))}
          </div>
          <a className="g-user__item" href={publicSiteUrl('/profile')} role="menuitem">
            <span className="mdi mdi-account-outline" aria-hidden /> {t('compte.mon_profil')}
          </a>
          <a className="g-user__item" href={publicSiteUrl('/')} role="menuitem">
            <span className="mdi mdi-open-in-new" aria-hidden /> {t('compte.site_public')}
          </a>
          <button type="button" className="g-user__item" onClick={logout} role="menuitem">
            <span className="mdi mdi-logout" aria-hidden /> {t('compte.deconnexion')}
          </button>
        </div>
      )}
    </div>
  )
}
